import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useHasPerm } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Award, DollarSign, CheckCircle2, AlertCircle, Clock, RefreshCw, Undo2 } from "lucide-react";
import { toast } from "sonner";
import UsageTip from "@/components/UsageTip";
import PDFExportButton from "@/components/PDFExportButton";

/**
 * Panel de Liquidación de Comisiones — vista del director/liquidador.
 * Muestra cuánto le debe a cada vendedor por comisiones ya causadas.
 *
 * Filtros:
 *  - Vendedor específico
 *  - Solo pendientes de pago
 *  - Rango de fechas
 */

const formatCOP = (n) => `$${Math.round(n || 0).toLocaleString("es-CO")}`;

const ESTADO_META = {
  pendiente_pago_cliente: { label: "Cliente no ha pagado", color: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
  pendiente_liquidacion: { label: "Falta pagar al vendedor", color: "bg-rose-100 text-rose-800 border-rose-300", icon: AlertCircle },
  liquidada: { label: "Liquidada", color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
};

export default function LiquidacionComisionesPage() {
  const canLiquidar = useHasPerm("comisiones.liquidar");
  const [data, setData] = useState(null);
  const [vendedores, setVendedores] = useState([]);
  const [vendedorId, setVendedorId] = useState("");
  const [soloPendientes, setSoloPendientes] = useState(true);
  const [seleccion, setSeleccion] = useState(new Set());
  const [payDialog, setPayDialog] = useState(false);
  const [payForm, setPayForm] = useState({ metodo: "transferencia", referencia: "", notas: "" });
  const [comprobante, setComprobante] = useState(null);
  const [loading, setLoading] = useState(false);
  const [batchIdRecien, setBatchIdRecien] = useState(null); // muestra los botones de descarga post-pago

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (vendedorId) params.set("vendedor_id", vendedorId);
      if (soloPendientes) params.set("solo_pendientes", "1");
      const { data } = await api.get(`/reportes/liquidacion-comisiones?${params}`);
      setData(data);
      setSeleccion(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vendedorId, soloPendientes]);
  useEffect(() => { api.get("/team").then(({ data }) => setVendedores((data || []).filter((u) => u.role !== "admin"))).catch(() => {}); }, []);

  const toggleFila = (pid) => {
    const s = new Set(seleccion);
    if (s.has(pid)) s.delete(pid); else s.add(pid);
    setSeleccion(s);
  };
  const toggleTodos = () => {
    const pendientes = (data?.filas || []).filter((f) => f.estado_comision === "pendiente_liquidacion").map((f) => f.pedido_id);
    if (seleccion.size === pendientes.length) setSeleccion(new Set());
    else setSeleccion(new Set(pendientes));
  };

  const totalSeleccionado = useMemo(() => {
    return (data?.filas || []).filter((f) => seleccion.has(f.pedido_id)).reduce((s, f) => s + f.comision_total, 0);
  }, [seleccion, data]);

  const marcarPagadas = async () => {
    try {
      const fd = new FormData();
      fd.append("pedido_ids", JSON.stringify(Array.from(seleccion)));
      fd.append("metodo", payForm.metodo);
      if (payForm.referencia) fd.append("referencia", payForm.referencia);
      if (payForm.notas) fd.append("notas", payForm.notas);
      if (comprobante) fd.append("comprobante", comprobante);
      const { data } = await api.post("/reportes/liquidacion-comisiones/marcar-pagada", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.pedidos_marcados} comisiones marcadas como pagadas ✓`);
      setBatchIdRecien(data.batch_id);
      setPayDialog(false);
      setComprobante(null);
      setSeleccion(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo marcar");
    }
  };

  const descargarEstadoCuenta = async (batchId) => {
    try {
      const { data } = await api.get(`/reportes/liquidacion-comisiones/${batchId}/estado-cuenta`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `liquidacion_${batchId}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar el estado de cuenta"); }
  };

  const verComprobante = async (batchId) => {
    try {
      const { data } = await api.get(`/reportes/liquidacion-comisiones/${batchId}/comprobante`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "Esta liquidación no tiene comprobante adjunto" : "No se pudo abrir");
    }
  };

  const revertir = async (pid) => {
    if (!window.confirm("¿Revertir esta liquidación? Volverá a estado 'Falta pagar al vendedor'.")) return;
    try {
      await api.post(`/reportes/liquidacion-comisiones/revertir/${pid}`);
      toast.success("Liquidación revertida");
      load();
    } catch (e) { toast.error("No se pudo revertir"); }
  };

  const t = data?.total || { pendiente_pago_cliente: 0, pendiente_liquidacion: 0, liquidada: 0 };

  return (
    <div>
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Cuentas por pagar</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2 flex items-center gap-3">
            <Award className="w-8 h-8 text-primary" /> Liquidación de comisiones
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            ¿Cuánto le debes a cada vendedor por comisiones ya causadas? Aquí lo ves y marcas lo pagado.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <PDFExportButton
            endpoint="comisiones"
            filename="beem-comisiones"
            label="Exportar PDF"
            testid="export-comisiones-pdf"
            fields={[
              { key: "desde", label: "Desde", type: "date" },
              { key: "hasta", label: "Hasta", type: "date" },
              { key: "vendedor_id", label: "Vendedor", type: "select", optionsFrom: "vendedores" },
              { key: "estado_comision", label: "Estado de la comisión", type: "select", options: [
                { value: "liquidada", label: "Liquidada (ya pagada al vendedor)" },
                { value: "pendiente_liquidacion", label: "Por cobrar (cliente pagó, falta pagarle al vendedor)" },
                { value: "pendiente_pago_cliente", label: "Bloqueada (cliente aún debe)" },
              ] },
            ]}
          />
          <Button onClick={load} variant="outline" className="h-11" data-testid="refresh-liq">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refrescar
          </Button>
        </div>
      </header>

      <UsageTip storageKey="liquidacion-comisiones" steps={[
        "Un pedido genera comisión cuando <b>el cliente termina de pagarlo</b>.",
        "Selecciona los pedidos y presiona <b>Marcar como pagada</b> para liquidarle al vendedor.",
        "El vendedor recibe una notificación con el detalle de qué pedidos y con qué método.",
      ]} />

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <KPI label="Pendiente que el cliente pague" value={formatCOP(t.pendiente_pago_cliente)} tone="amber" icon={Clock} />
        <KPI label="Falta pagar al vendedor" value={formatCOP(t.pendiente_liquidacion)} tone="rose" icon={AlertCircle} testid="kpi-por-pagar" />
        <KPI label="Ya liquidadas" value={formatCOP(t.liquidada)} tone="emerald" icon={CheckCircle2} />
      </div>

      {/* Filtros */}
      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <select value={vendedorId} onChange={(e) => setVendedorId(e.target.value)} className="h-11 border border-border rounded-md px-3 bg-card text-sm min-w-[220px]" data-testid="filtro-vendedor">
          <option value="">Todos los vendedores</option>
          {vendedores.map((v) => <option key={v.id} value={v.id}>{v.name || v.email}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={soloPendientes} onChange={(e) => setSoloPendientes(e.target.checked)} className="w-4 h-4" data-testid="filtro-pendientes" />
          Solo pendientes
        </label>
        {seleccion.size > 0 && canLiquidar && (
          <div className="ml-auto flex items-center gap-3 bg-primary/10 border border-primary/30 rounded-lg px-4 py-2">
            <div className="text-sm">
              <span className="font-semibold">{seleccion.size}</span> pedidos ·{" "}
              <span className="font-bold text-primary tabular-nums">{formatCOP(totalSeleccionado)}</span>
            </div>
            <Button onClick={() => setPayDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9" data-testid="btn-marcar-pagadas">
              <DollarSign className="w-4 h-4 mr-1" /> Marcar como pagada
            </Button>
          </div>
        )}
      </div>

      {/* Resumen por vendedor */}
      {data?.resumen?.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-4 mb-4">
          <div className="text-sm font-semibold text-foreground mb-3">Resumen por vendedor</div>
          <div className="space-y-2">
            {data.resumen.map((v) => (
              <div key={v.vendedor_id} className="grid grid-cols-12 gap-2 items-center text-sm">
                <div className="col-span-4 font-medium truncate">{v.vendedor_nombre || "—"}</div>
                <div className="col-span-2 text-muted-foreground text-xs">{v.pedidos} pedidos</div>
                <div className="col-span-2 text-right text-amber-700 tabular-nums text-xs">{formatCOP(v.pendiente_pago_cliente)}</div>
                <div className="col-span-2 text-right text-rose-700 tabular-nums font-semibold">{formatCOP(v.pendiente_liquidacion)}</div>
                <div className="col-span-2 text-right text-emerald-700 tabular-nums text-xs">{formatCOP(v.liquidada)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabla detalle */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="liq-table">
            <thead className="bg-muted/40 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="p-3 w-8">
                  {canLiquidar && <input type="checkbox" onChange={toggleTodos} checked={seleccion.size > 0 && seleccion.size === (data?.filas || []).filter((f) => f.estado_comision === "pendiente_liquidacion").length} className="w-4 h-4" />}
                </th>
                <th className="text-left p-3">Pedido</th>
                <th className="text-left p-3">Vendedor</th>
                <th className="text-left p-3">Cliente</th>
                <th className="text-right p-3">Total pedido</th>
                <th className="text-right p-3">Comisión</th>
                <th className="text-left p-3">Estado</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(data?.filas || []).length === 0 && (
                <tr><td colSpan={8} className="text-center py-16 text-muted-foreground">
                  {loading ? "Cargando..." : "No hay comisiones para mostrar en este filtro"}
                </td></tr>
              )}
              {(data?.filas || []).map((f) => {
                const m = ESTADO_META[f.estado_comision];
                const Icon = m.icon;
                const puedeSel = f.estado_comision === "pendiente_liquidacion" && canLiquidar;
                return (
                  <tr key={f.pedido_id} className="hover:bg-accent/30 transition-colors" data-testid={`liq-row-${f.pedido_id}`}>
                    <td className="p-3">
                      {puedeSel && (
                        <input type="checkbox" checked={seleccion.has(f.pedido_id)} onChange={() => toggleFila(f.pedido_id)} className="w-4 h-4" data-testid={`sel-${f.pedido_id}`} />
                      )}
                    </td>
                    <td className="p-3">
                      <div className="font-mono font-semibold text-foreground">{f.pedido_numero}</div>
                      <div className="text-xs text-muted-foreground">{f.fecha}</div>
                    </td>
                    <td className="p-3 text-foreground/80">{f.vendedor_nombre}</td>
                    <td className="p-3 text-foreground/80 truncate max-w-[200px]">{f.cliente_nombre}</td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">{formatCOP(f.total_pedido)}</td>
                    <td className="p-3 text-right tabular-nums font-semibold text-primary">{formatCOP(f.comision_total)}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border ${m.color}`}>
                        <Icon className="w-3 h-3" /> {m.label}
                      </span>
                      {f.liquidada_at && <div className="text-[10px] text-muted-foreground mt-1">Pagado {f.liquidada_at.slice(0, 10)} · {f.metodo_pago_vendedor}</div>}
                    </td>
                    <td className="p-3">
                      {f.estado_comision === "liquidada" && canLiquidar && (
                        <button onClick={() => revertir(f.pedido_id)} className="text-[11px] text-muted-foreground hover:text-rose-600 flex items-center gap-1" title="Revertir liquidación">
                          <Undo2 className="w-3 h-3" /> Revertir
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={payDialog} onOpenChange={setPayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marcar {seleccion.size} comisiones como pagadas</DialogTitle>
            <p className="text-sm text-muted-foreground">Total: <b className="text-primary">{formatCOP(totalSeleccionado)}</b></p>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            <div>
              <label className="text-xs text-muted-foreground">Método de pago al vendedor</label>
              <select value={payForm.metodo} onChange={(e) => setPayForm({ ...payForm, metodo: e.target.value })} className="w-full h-11 border border-border rounded-md px-3 bg-card text-sm mt-1">
                <option value="transferencia">Transferencia</option>
                <option value="efectivo">Efectivo</option>
                <option value="nequi">Nequi</option>
                <option value="daviplata">Daviplata</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Referencia (opcional)</label>
              <Input value={payForm.referencia} onChange={(e) => setPayForm({ ...payForm, referencia: e.target.value })} placeholder="Ej: TRF-12345" className="h-11 mt-1" data-testid="pay-referencia" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Notas (opcional)</label>
              <textarea value={payForm.notas} onChange={(e) => setPayForm({ ...payForm, notas: e.target.value })} className="w-full border border-border rounded-md px-3 py-2 text-sm mt-1 min-h-[80px]" placeholder="Ej: Corresponde al mes de febrero" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Comprobante (imagen o PDF · opcional)</label>
              <label className="mt-1 flex items-center gap-3 h-12 px-3 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 bg-card">
                <RefreshCw className="w-4 h-4 text-muted-foreground/70" />
                <span className="flex-1 text-sm text-muted-foreground truncate">
                  {comprobante ? <b className="text-foreground">{comprobante.name}</b> : "Adjunta foto o PDF de la transferencia (visible para el vendedor)"}
                </span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setComprobante(e.target.files?.[0] || null)}
                  className="hidden"
                  data-testid="pay-comprobante-input"
                />
              </label>
            </div>
            <Button onClick={marcarPagadas} className="w-full bg-emerald-600 hover:bg-emerald-700 h-11" data-testid="pay-confirm">
              <DollarSign className="w-4 h-4 mr-2" /> Confirmar pago y notificar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Banner de éxito post-liquidación con acciones rápidas */}
      <Dialog open={!!batchIdRecien} onOpenChange={(v) => { if (!v) setBatchIdRecien(null); }}>
        <DialogContent data-testid="post-liq-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" /> Liquidación registrada
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Guarda el estado de cuenta y compártelo con el vendedor si lo necesita.
            </p>
          </DialogHeader>
          <div className="grid gap-2 mt-4">
            <Button onClick={() => descargarEstadoCuenta(batchIdRecien)} variant="outline" className="h-11 justify-start" data-testid="post-liq-pdf">
              📄 Descargar estado de cuenta (PDF)
            </Button>
            <Button onClick={() => verComprobante(batchIdRecien)} variant="outline" className="h-11 justify-start" data-testid="post-liq-comp">
              🖼️ Ver comprobante adjunto
            </Button>
            <div className="text-[11px] text-muted-foreground text-center mt-2">
              Batch: <code className="font-mono">{batchIdRecien}</code>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, tone, icon: Icon, testid }) {
  const tones = {
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  };
  return (
    <div className={`rounded-xl border-2 p-5 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold">{label}</div>
      </div>
      <div className="text-3xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}
